import { Button } from '@calizahq/react-components-v2'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { useAlertContext } from '@/context'
import { TicketMutations } from '@/graphql/mutations/ticket.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { TicketDetail } from '@/types/Ticket'

type Props = {
  childTickets?: TicketDetail['childTickets']
  refetchDetails: () => void
  ticketId: number
  toggle: () => void
}

export const CloseTicket = ({ toggle, ticketId, refetchDetails, childTickets }: Props) => {
  const { isTablet } = useMobile()
  const { setAlert } = useAlertContext()
  const [closeError, setCloseError] = useState<string | null>(null)

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(TicketMutations.updateTicket(), {
    onSuccess: () => {
      toggle()
      refetchDetails()
      setAlert('Ticket closed', 'success')
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.errors?.[0]?.message
      if (errorMessage && errorMessage.startsWith('Cannot close a ticket with open child tickets')) {
        setCloseError('Cannot close this ticket while it has open child tickets. Please close all child tickets first.')
      } else {
        setAlert(errorMessage || 'Failed to close ticket', 'error')
      }
    },
  })

  const handleCloseTicket = () => {
    setCloseError(null)
    mutate({
      ticket: {
        ticketId,
        closed: true,
      },
    })
  }

  const openChildTickets = childTickets?.filter((child) => child.internalTicketStatus !== 'Closed') || []

  return (
    <div className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
      <h5 className="mb-4 text-header-sm leading-120 md:text-header-md">Are you sure you want to close this ticket?</h5>
      <p className="mb-4 text-p-xs md:mb-8">Once you close a ticket, you won&apos;t be able to edit it again.</p>
      {closeError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="mb-2 text-p-sm text-text-semantic-error">{closeError}</p>
          {openChildTickets.length > 0 && (
            <ul className="list-inside list-disc">
              {openChildTickets.map((child) => (
                <li className="text-p-sm" key={child.id}>
                  <Link className="underline text-link" to={`/tickets/${child.id}`}>
                    #{child.id} - {child.subject}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex flex-col-reverse justify-between md:flex-row">
        <Button
          className="w-full bg-action-secondary md:flex md:w-auto"
          onClick={toggle}
          size={isTablet ? 'medium' : 'small'}
          variant="secondary"
        >
          Close
        </Button>
        <Button
          className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
          disabled={isMutationLoading}
          isLoading={isMutationLoading}
          onClick={handleCloseTicket}
          size={isTablet ? 'medium' : 'small'}
        >
          Close Ticket
        </Button>
      </div>
    </div>
  )
}
